import type { ReactNode } from "react";
import { AlertTriangle, ArrowDown, ArrowRight, Check } from "lucide-react";
import type { Ticket } from "@/modules/types";
import {
  ticketStatusLabel,
  ticketStatusVariant,
} from "@/modules/tickets/ticketInboxConfig";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TICKET_MERGE_STATUS_DOT } from "@/modules/tickets/ticketMergeCompare";

/** Two-step indicator shown under the merge dialog title on every step. */
export const TicketMergeSteps = ({
  steps,
  current,
}: {
  steps: string[];
  current: number;
}) => (
  <ol className="flex items-center gap-2 pt-1 text-xs">
    {steps.map((label, index) => {
      const done = index < current;
      const active = index === current;
      return (
        <li key={label} className="flex items-center gap-2">
          {index > 0 ? (
            <span className="h-px w-6 bg-border" aria-hidden />
          ) : null}
          <span
            className={cn(
              "flex size-5 items-center justify-center rounded-full border text-2xs font-semibold",
              active && "border-primary bg-primary text-primary-foreground",
              done && "border-primary/40 bg-primary/10 text-primary",
              !active && !done && "border-border text-muted-foreground",
            )}
          >
            {done ? <Check className="size-3" /> : index + 1}
          </span>
          <span
            className={cn(
              "font-medium",
              active ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

export const TicketMergeTicketCard = ({
  ticket,
  role,
}: {
  ticket: Ticket;
  role: "primary" | "secondary";
}) => (
  <div
    className={cn(
      "space-y-2 rounded-lg border p-4",
      role === "primary"
        ? "border-primary/30 bg-primary/[0.03]"
        : "border-border bg-muted/20",
    )}
  >
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs text-muted-foreground">
        #{ticket.id}
      </span>
      {role === "primary" ? (
        <Badge variant="secondary" className="h-5 px-2 text-2xs">
          Primary
        </Badge>
      ) : (
        <span className="text-2xs font-medium text-muted-foreground">
          Will be resolved
        </span>
      )}
    </div>
    <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
      {ticket.subject}
    </p>
    <div className="flex items-center gap-1.5">
      <span
        className={cn(
          "size-2 rounded-full",
          TICKET_MERGE_STATUS_DOT[ticket.status] ?? "bg-muted-foreground/60",
        )}
      />
      <Badge
        variant={ticketStatusVariant(ticket.status)}
        className="h-5 px-2 text-2xs capitalize"
      >
        {ticketStatusLabel(ticket.status)}
      </Badge>
    </div>
  </div>
);

/** Secondary ticket(s) → primary ticket, using the same card on both sides. */
export const TicketMergeFlow = ({
  primary,
  secondaries,
}: {
  primary: Ticket;
  secondaries: Ticket[];
}) => (
  <div className="grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
    <div className="max-h-64 space-y-2 overflow-y-auto">
      {secondaries.map((ticket) => (
        <TicketMergeTicketCard
          key={String(ticket.id)}
          ticket={ticket}
          role="secondary"
        />
      ))}
    </div>
    <div className="flex justify-center text-muted-foreground" aria-hidden>
      <ArrowRight className="hidden size-5 md:block" />
      <ArrowDown className="size-5 md:hidden" />
    </div>
    <TicketMergeTicketCard ticket={primary} role="primary" />
  </div>
);

/** Section wrapper matching the compare table container. */
export const TicketMergeSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
    <div className="border-b border-border bg-muted/35 px-5 py-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
      {title}
    </div>
    {children}
  </section>
);

/** What happens on merge + irreversible warning, shared by both merge dialogs. */
export const TicketMergeOutcome = ({ items }: { items: string[] }) => (
  <div className="space-y-3">
    <ul className="space-y-1.5 text-sm text-muted-foreground">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <Check className="mt-0.5 size-4 shrink-0 text-success" />
          {item}
        </li>
      ))}
    </ul>
    <p className="flex items-center gap-2 text-sm font-medium text-destructive">
      <AlertTriangle className="size-4 shrink-0" />
      This action cannot be undone.
    </p>
  </div>
);
